'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import type { ReactNode } from 'react';

interface CarouselUrlsContextValue {
  subscribe: (listener: () => void) => () => void;
  has: (url: string) => boolean;
  register: (url: string) => () => void;
}

const noopUnsubscribe = () => {};

const CarouselUrlsContext = createContext<CarouselUrlsContextValue>({
  subscribe: () => noopUnsubscribe,
  has: () => false,
  register: () => noopUnsubscribe,
});

export function CarouselUrlsProvider({ children }: { children: ReactNode }) {
  // Ref-based store + manual subscription so registering a URL only re-renders
  // the `useIsCarouselUrl` consumers whose URL actually flipped — not every
  // MarkdownLink in the thread (which would cascade during streaming).
  const urlsRef = useRef<Set<string>>(new Set());
  const listenersRef = useRef<Set<() => void>>(new Set());

  const value = useMemo<CarouselUrlsContextValue>(() => {
    const notify = () => listenersRef.current.forEach(cb => cb());
    return {
      subscribe(listener) {
        listenersRef.current.add(listener);
        return () => {
          listenersRef.current.delete(listener);
        };
      },
      has(url) {
        return urlsRef.current.has(url);
      },
      register(url) {
        if (urlsRef.current.has(url)) return noopUnsubscribe;
        urlsRef.current.add(url);
        notify();
        return () => {
          if (!urlsRef.current.delete(url)) return;
          notify();
        };
      },
    };
  }, []);

  return <CarouselUrlsContext.Provider value={value}>{children}</CarouselUrlsContext.Provider>;
}

export function useRegisterCarouselUrl(url: string | null | undefined): void {
  const { register } = useContext(CarouselUrlsContext);

  useEffect(() => {
    if (!url) return;
    return register(url);
  }, [url, register]);
}

export function useIsCarouselUrl(url: string | null | undefined): boolean {
  const { subscribe, has } = useContext(CarouselUrlsContext);
  const getSnapshot = useCallback(() => (url ? has(url) : false), [url, has]);
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
